import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { THEMES, THEME_SELECTOR_ORDER, useTheme } from '../themeConfig';
import { FaCheckCircle, FaChevronDown, FaPalette } from 'react-icons/fa';

const getThemeSwatches = (theme) => [
  theme.accent,
  theme.accentMuted || theme.accentSoft,
  theme.bg,
].filter(Boolean);

const ThemeSelector = ({ onThemeSelect, currentSelectedThemeId }) => {
  const { theme: activeTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const orderedThemes = useMemo(
    () => THEME_SELECTOR_ORDER.map((themeId) => THEMES[themeId]).filter(Boolean),
    [],
  );
  const selectedTheme = THEMES[currentSelectedThemeId] || orderedThemes[0] || activeTheme;
  const visibleThemes = isOpen ? orderedThemes : [selectedTheme].filter(Boolean);

  const handleThemeSelect = (themeId) => {
    onThemeSelect(themeId);
    setIsOpen(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="lumirae-ds-panel w-full space-y-5 p-5 mx-auto"
      style={{
        '--lumirae-accent-primary': activeTheme.accent,
        '--lumirae-accent-soft': activeTheme.accentSoft,
        '--lumirae-surface-raised': activeTheme.cardBg,
        '--lumirae-border-default': activeTheme.cardBorder,
        '--lumirae-text-primary': activeTheme.text,
        '--lumirae-text-secondary': activeTheme.secondaryText,
      }}
    >
      <div className="text-left sm:text-center">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full border border-white/70 bg-white/70 shadow-sm">
          <FaPalette style={{ color: activeTheme.accent }} />
        </div>
        <div className="lumirae-ds-meta" style={{ color: activeTheme.accent }}>
          Color Mood
        </div>
        <h3 className="mt-2 text-2xl font-black leading-tight tracking-tight" style={{ color: activeTheme.text }}>
          Choose the feeling
        </h3>
        <p className="mx-auto mt-2 max-w-sm text-xs font-bold leading-relaxed" style={{ color: activeTheme.secondaryText }}>
          Pick a visual mood for the gift. The modules stay consistent, while color, opening, and details follow the theme.
        </p>
      </div>

      <div className="lumirae-mood-picker-list">
        {visibleThemes.map((theme) => {
          const selected = currentSelectedThemeId === theme.id;
          return (
          <motion.button
            key={theme.id}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => (isOpen ? handleThemeSelect(theme.id) : setIsOpen(true))}
            className="lumirae-mood-card"
            style={{
              background: `linear-gradient(145deg, ${theme.cardBg}, ${theme.bg})`,
              borderColor: selected ? activeTheme.accent : theme.cardBorder,
              color: theme.text,
              boxShadow: selected
                ? `0 18px 36px ${theme.cardShadow || 'rgba(33,27,29,0.12)'}`
                : '0 10px 24px rgba(33,27,29,0.06)',
            }}
          >
            <div className="lumirae-mood-card-swatches">
              {getThemeSwatches(theme).slice(0, 4).map((color, index) => (
                <span key={`${theme.id}_${index}`} style={{ backgroundColor: color }} />
              ))}
            </div>
            <div className="min-w-0 flex-1">
              <span className="lumirae-mood-card-title" style={{ color: theme.text }}>
                {theme.name}
              </span>
              <span className="lumirae-mood-card-copy" style={{ color: theme.secondaryText || theme.text }}>
                {theme.mood}
              </span>
            </div>
            {selected && (
              <FaCheckCircle className="shrink-0 text-lg" style={{ color: activeTheme.accent }} />
            )}
          </motion.button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className="lumirae-ds-button lumirae-ds-button-secondary flex w-full items-center justify-center gap-2 px-4 py-3"
      >
        {isOpen ? 'Hide Color Moods' : 'Change Color Mood'}
        <FaChevronDown className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
    </motion.div>
  );
};

export default ThemeSelector;
